import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <Navbar />
      
      <div className="pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl p-8 sm:p-12 shadow-sm border border-gray-100 dark:border-neutral-800">
            <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-neutral-100 mb-8">Privacy Policy</h1>
            <p className="text-gray-500 dark:text-neutral-500 mb-8">Last updated: February 4, 2026</p>
            
            <div className="prose prose-gray max-w-none">
              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">1. Introduction</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  Welcome to Connectify eSIM ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
                </p>
                <p className="text-gray-600 dark:text-neutral-400">
                  By using our services, you agree to the collection and use of information in accordance with this policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">2. Information We Collect</h2>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-neutral-200 mb-3">2.1 Personal Information</h3>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">We may collect personal information that you voluntarily provide to us when you:</p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 mb-4 space-y-2">
                  <li>Register for an account</li>
                  <li>Make a purchase</li>
                  <li>Sign up for our newsletter</li>
                  <li>Contact our customer support</li>
                  <li>Use social login features (Google, Facebook)</li>
                </ul>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">This information may include:</p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 mb-4 space-y-2">
                  <li>Name (first and last)</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Billing address</li>
                  <li>Payment information (processed securely through Stripe)</li>
                  <li>Profile picture (when using social login)</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-800 dark:text-neutral-200 mb-3">2.2 Automatically Collected Information</h3>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">When you access our services, we may automatically collect:</p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>Device information (type, operating system, browser)</li>
                  <li>IP address and location data</li>
                  <li>Usage data and browsing patterns</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">We use the information we collect to:</p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>Process and fulfill your eSIM orders</li>
                  <li>Create and manage your account</li>
                  <li>Provide customer support and respond to inquiries</li>
                  <li>Send transactional communications (order confirmations, eSIM details)</li>
                  <li>Improve our services and user experience</li>
                  <li>Detect and prevent fraud or unauthorized access</li>
                  <li>Comply with legal obligations</li>
                  <li>Send marketing communications (with your consent)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">4. Information Sharing and Disclosure</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">We may share your information with:</p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li><strong>Service Providers:</strong> Third-party vendors who assist in providing our services (payment processors, eSIM providers, hosting services)</li>
                  <li><strong>Business Partners:</strong> eSIM network providers to activate and manage your eSIM service</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                </ul>
                <p className="text-gray-600 dark:text-neutral-400 mt-4">
                  We do not sell your personal information to third parties.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">5. Data Security</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  We implement appropriate technical and organizational security measures to protect your personal information, including:
                </p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>Encryption of data in transit (SSL/TLS)</li>
                  <li>Secure payment processing through PCI-compliant providers</li>
                  <li>Regular security assessments</li>
                  <li>Access controls and authentication measures</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">6. Your Privacy Rights</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">Depending on your location, you may have the right to:</p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing of your data</li>
                  <li>Data portability</li>
                  <li>Withdraw consent</li>
                </ul>
                <p className="text-gray-600 dark:text-neutral-400 mt-4">
                  To exercise these rights, please contact us at privacy@connectify-esims.com.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">7. Cookies and Tracking</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  We use cookies and similar tracking technologies to enhance your experience. You can control cookie preferences through your browser settings. Types of cookies we use:
                </p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li><strong>Essential Cookies:</strong> Required for basic site functionality</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                  <li><strong>Functional Cookies:</strong> Remember your preferences</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">8. Third-Party Services</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">Our services integrate with third-party platforms including:</p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li><strong>Google:</strong> For authentication and sign-in</li>
                  <li><strong>Facebook:</strong> For authentication and sign-in</li>
                  <li><strong>Stripe:</strong> For secure payment processing</li>
                </ul>
                <p className="text-gray-600 dark:text-neutral-400 mt-4">
                  These services have their own privacy policies, and we encourage you to review them.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">9. Data Retention</h2>
                <p className="text-gray-600 dark:text-neutral-400">
                  We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. When data is no longer needed, we securely delete or anonymize it.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">10. Children's Privacy</h2>
                <p className="text-gray-600 dark:text-neutral-400">
                  Our services are not intended for children under 16 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">11. International Data Transfers</h2>
                <p className="text-gray-600 dark:text-neutral-400">
                  Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">12. Changes to This Policy</h2>
                <p className="text-gray-600 dark:text-neutral-400">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">13. Contact Us</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <ul className="list-none text-gray-600 dark:text-neutral-400 space-y-2">
                  <li><strong>Email:</strong> hello@connectify-esims.com</li>
                  <li><strong>Website:</strong> www.connectify-esims.com</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
